import { INetworkRequest, INetworkResponse, INetworkInterceptor } from '../types';
import { generateUniqueId } from '../utils';
import {
  INetworkTiming,
  IGraphQLInfo,
  IEnhancedNetworkRequest,
  IEnhancedNetworkResponse,
  INetworkInterceptorConfig
} from './types';

/**
 * 고급 네트워크 인터셉터
 * 
 * Fetch, XHR을 지원하며 다음 기능을 제공합니다:
 * - 상세한 타이밍 정보 수집
 * - GraphQL 쿼리 감지 및 파싱
 * - 요청/응답 크기 측정
 * - 네트워크 에러 추적
 */
export class NetworkInterceptor implements INetworkInterceptor {
  private _config: Required<Omit<INetworkInterceptorConfig, 'onRequest' | 'onResponse' | 'onError'>> & {
    onRequest?: (request: IEnhancedNetworkRequest) => void;
    onResponse?: (response: IEnhancedNetworkResponse) => void;
    onError?: (error: Error, request: IEnhancedNetworkRequest) => void;
  };

  private _isIntercepting: boolean = false;
  private _requestHistory: IEnhancedNetworkRequest[] = [];
  private _responseHistory: IEnhancedNetworkResponse[] = [];

  // 원본 함수들 저장
  private _originalFetch: typeof fetch;
  private _originalXMLHttpRequest: typeof XMLHttpRequest;

  constructor(config: Partial<INetworkInterceptorConfig> = {}) {
    this._config = {
      maxHistorySize: 1000,
      captureRequestBody: true,
      captureResponseBody: true,
      enableTiming: true,
      enableGraphQLParsing: true,
      enableAxiosInterception: false,
      maxBodySize: 1024 * 1024, // 1MB
      excludePatterns: [
        /chrome-extension:/,
        /moz-extension:/,
        /webkit-masked-url:/,
        /^data:/,
        /\.woff2?(\?|$)/,
        /\.ttf(\?|$)/,
        /favicon\.ico/,
      ],
      includePatterns: [],
      onRequest: undefined,
      onResponse: undefined,
      onError: undefined,
      ...config,
    };

    this._originalFetch = window.fetch.bind(window);
    this._originalXMLHttpRequest = window.XMLHttpRequest;
  }

  /**
   * 네트워크 요청 가로채기 시작
   */
  public startIntercepting(): void {
    if (this._isIntercepting) {
      console.warn('[NetworkInterceptor] Already intercepting');
      return;
    }

    console.log('[NetworkInterceptor] Starting network interception...');

    this._interceptFetch();
    this._interceptXMLHttpRequest();

    this._isIntercepting = true;
    console.log('[NetworkInterceptor] Network interception started');
  }

  /**
   * 네트워크 요청 가로채기 중단
   */
  public stopIntercepting(): void {
    if (!this._isIntercepting) {
      return;
    }

    console.log('[NetworkInterceptor] Stopping network interception...');

    // 원본 함수들 복원
    window.fetch = this._originalFetch;
    window.XMLHttpRequest = this._originalXMLHttpRequest;

    this._isIntercepting = false;
    console.log('[NetworkInterceptor] Network interception stopped');
  }

  /**
   * 요청 히스토리 가져오기
   */
  public getRequestHistory(): IEnhancedNetworkRequest[] {
    return [...this._requestHistory];
  }

  /**
   * 응답 히스토리 가져오기
   */
  public getResponseHistory(): IEnhancedNetworkResponse[] {
    return [...this._responseHistory];
  }

  /**
   * 히스토리 초기화
   */
  public clearHistory(): void {
    this._requestHistory = [];
    this._responseHistory = [];
  }

  /**
   * 네트워크 성능 매트릭 가져오기
   */
  public getNetworkMetrics() {
    const requests = this._requestHistory;
    const responses = this._responseHistory;

    if (requests.length === 0) {
      return {
        totalRequests: 0,
        successRate: 0,
        averageResponseTime: 0,
        totalDataTransferred: 0,
        errorCount: 0,
        slowRequestsCount: 0,
      };
    }

    const completedRequests = requests.filter(req => 
      responses.some(res => res.requestId === req.id)
    );

    const totalDataTransferred = responses.reduce((sum, res) => 
      sum + res.size.total, 0
    );

    const averageResponseTime = completedRequests.reduce((sum, req) => 
      sum + req.timing.duration, 0
    ) / completedRequests.length;

    const errorCount = responses.filter(res => res.status >= 400).length;
    const slowRequestsCount = completedRequests.filter(req => 
      req.timing.duration > 3000
    ).length;

    return {
      totalRequests: requests.length,
      completedRequests: completedRequests.length,
      successRate: completedRequests.length > 0 ? 
        ((completedRequests.length - errorCount) / completedRequests.length) * 100 : 0,
      averageResponseTime: Math.round(averageResponseTime),
      totalDataTransferred,
      errorCount,
      slowRequestsCount,
      graphqlRequestsCount: requests.filter(req => req.graphql).length,
    };
  }

  /**
   * Fetch API 가로채기
   */
  private _interceptFetch(): void {
    const self = this;
    
    window.fetch = async function(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const url = typeof input === 'string' ? input : 
                  input instanceof URL ? input.toString() : input.url;

      // 제외 패턴 확인
      if (self._shouldExcludeRequest(url)) {
        return self._originalFetch.call(this, input, init);
      }

      const requestId = generateUniqueId();
      const method = init?.method || 'GET';
      const startTime = performance.now();

      // 요청 헤더 추출
      const headers = self._extractHeaders(init?.headers);
      
      // 요청 바디 처리
      let requestBody: string | undefined;
      let requestBodySize = 0;
      
      if (init?.body && self._config.captureRequestBody) {
        if (typeof init.body === 'string') {
          requestBody = init.body.length > self._config.maxBodySize ? 
            init.body.substring(0, self._config.maxBodySize) + '...[truncated]' : 
            init.body;
          requestBodySize = new TextEncoder().encode(init.body).length;
        } else {
          requestBody = '[Binary Data]';
          requestBodySize = (init.body as any)?.length || 0;
        }
      }

      // GraphQL 감지
      const graphqlInfo = self._parseGraphQL(headers, requestBody);

      // 타이밍 정보 초기화
      const timing: INetworkTiming = {
        startTime,
        requestStart: startTime,
        responseStart: 0,
        responseEnd: 0,
        duration: 0,
      };

      // 요청 객체 생성
      const networkRequest: IEnhancedNetworkRequest = {
        id: requestId,
        url,
        method,
        headers,
        body: requestBody,
        timestamp: Date.now(),
        timing,
        size: {
          requestHeaders: self._calculateHeadersSize(headers),
          requestBody: requestBodySize,
          total: self._calculateHeadersSize(headers) + requestBodySize,
        },
        graphql: graphqlInfo,
        priority: self._calculateRequestPriority(url, method, graphqlInfo),
      };

      self._requestHistory.push(networkRequest);

      // 히스토리 크기 제한
      if (self._requestHistory.length > self._config.maxHistorySize) {
        self._requestHistory = self._requestHistory.slice(-self._config.maxHistorySize);
      }

      // 콜백 호출
      if (self._config.onRequest) {
        try {
          self._config.onRequest(networkRequest);
        } catch (error) {
          console.warn('[NetworkInterceptor] Error in onRequest callback:', error);
        }
      }

      try {
        timing.responseStart = performance.now();
        const response = await self._originalFetch.call(this, input, init);
        timing.responseEnd = performance.now();
        timing.duration = timing.responseEnd - timing.startTime;

        // 응답 처리
        await self._handleFetchResponse(response, networkRequest);

        return response;
      } catch (error) {
        timing.responseEnd = performance.now();
        timing.duration = timing.responseEnd - timing.startTime;

        // 에러 응답 생성
        await self._handleFetchError(error as Error, networkRequest);

        throw error;
      }
    };
  }

  /**
   * XMLHttpRequest 가로채기
   */
  private _interceptXMLHttpRequest(): void {
    const self = this;
    const OriginalXHR = this._originalXMLHttpRequest;

    (window as any).XMLHttpRequest = function XMLHttpRequestWrapper() {
      const xhr = new OriginalXHR();
      let requestId = '';
      let url = '';
      let method = '';
      let requestHeaders: Record<string, string> = {};
      let startTime = 0;

      const originalOpen = xhr.open;
      const originalSend = xhr.send;
      const originalSetRequestHeader = xhr.setRequestHeader;

      xhr.open = function(httpMethod: string, httpUrl: string | URL, ...args: unknown[]) {
        method = httpMethod;
        url = httpUrl.toString();
        requestId = generateUniqueId();
        
        return originalOpen.apply(this, [httpMethod, httpUrl, ...args] as Parameters<typeof originalOpen>);
      };

      xhr.setRequestHeader = function(name: string, value: string) {
        requestHeaders[name] = value;
        return originalSetRequestHeader.call(this, name, value);
      };

      xhr.send = function(body?: Document | XMLHttpRequestBodyInit | null) {
        // 제외 패턴 확인
        if (self._shouldExcludeRequest(url)) {
          return originalSend.call(this, body);
        }

        startTime = performance.now();

        // 요청 바디 처리
        let requestBody: string | undefined;
        let requestBodySize = 0;

        if (body && self._config.captureRequestBody) {
          if (typeof body === 'string') {
            requestBody = body.length > self._config.maxBodySize ? 
              body.substring(0, self._config.maxBodySize) + '...[truncated]' : 
              body;
            requestBodySize = new TextEncoder().encode(body).length;
          } else {
            requestBody = '[Binary Data]';
            requestBodySize = (body as any)?.length || 0;
          }
        }

        // GraphQL 감지
        const graphqlInfo = self._parseGraphQL(requestHeaders, requestBody);

        // 타이밍 정보
        const timing: INetworkTiming = {
          startTime,
          requestStart: startTime,
          responseStart: 0,
          responseEnd: 0,
          duration: 0,
        };

        const networkRequest: IEnhancedNetworkRequest = {
          id: requestId,
          url,
          method,
          headers: requestHeaders,
          body: requestBody,
          timestamp: Date.now(),
          timing,
          size: {
            requestHeaders: self._calculateHeadersSize(requestHeaders),
            requestBody: requestBodySize,
            total: self._calculateHeadersSize(requestHeaders) + requestBodySize,
          },
          graphql: graphqlInfo,
          priority: self._calculateRequestPriority(url, method, graphqlInfo),
        };

        self._requestHistory.push(networkRequest);

        // 콜백 호출
        if (self._config.onRequest) {
          try {
            self._config.onRequest(networkRequest);
          } catch (error) {
            console.warn('[NetworkInterceptor] Error in onRequest callback:', error);
          }
        }

        // 응답 처리
        const originalOnReadyStateChange = xhr.onreadystatechange;
        xhr.onreadystatechange = function(event: Event) {
          if (xhr.readyState === 4) {
            timing.responseEnd = performance.now();
            timing.duration = timing.responseEnd - timing.startTime;

            self._handleXHRResponse(xhr, networkRequest);
          }

          if (originalOnReadyStateChange) {
            return originalOnReadyStateChange.call(this, event);
          }
        };

        return originalSend.call(this, body);
      };

      return xhr;
    } as any;

    // Static 속성들 복사
    Object.setPrototypeOf((window as any).XMLHttpRequest, OriginalXHR);
    Object.defineProperty((window as any).XMLHttpRequest, 'prototype', {
      value: OriginalXHR.prototype,
      writable: false
    });
  }

  /**
   * 요청 URL이 제외 패턴에 매치되는지 확인
   */
  private _shouldExcludeRequest(url: string): boolean {
    return this._config.excludePatterns.some(pattern => pattern.test(url));
  }

  /**
   * GraphQL 쿼리 파싱
   */
  private _parseGraphQL(headers: Record<string, string>, body?: string): IGraphQLInfo | undefined {
    if (!this._config.enableGraphQLParsing || !body) {
      return undefined;
    }

    try {
      // Content-Type이 GraphQL인지 확인
      const contentType = headers['content-type'] || headers['Content-Type'] || '';
      if (contentType.includes('application/graphql')) {
        // Raw GraphQL query
        return {
          operationType: this._detectGraphQLOperationType(body),
          operationName: this._extractGraphQLOperationName(body),
        };
      }

      // JSON 형태의 GraphQL 요청인지 확인
      if (contentType.includes('application/json')) {
        const parsed = JSON.parse(body);
        if (parsed.query || parsed.operationName) {
          return {
            operationType: this._detectGraphQLOperationType(parsed.query),
            operationName: parsed.operationName,
            variables: parsed.variables,
            extensions: parsed.extensions,
          };
        }
      }

      return undefined;
    } catch (error) {
      return undefined;
    }
  }

  /**
   * GraphQL 오퍼레이션 타입 감지
   */
  private _detectGraphQLOperationType(query: string): 'query' | 'mutation' | 'subscription' {
    if (!query) return 'query';

    const trimmed = query.trim().toLowerCase();
    if (trimmed.startsWith('mutation')) return 'mutation';
    if (trimmed.startsWith('subscription')) return 'subscription';
    return 'query';
  }

  /**
   * GraphQL 오퍼레이션 이름 추출
   */
  private _extractGraphQLOperationName(query: string): string | undefined {
    if (!query) return undefined;

    const match = query.match(/(?:query|mutation|subscription)\s+(\w+)/);
    return match ? match[1] : undefined;
  }

  /**
   * 요청 우선순위 계산
   */
  private _calculateRequestPriority(url: string, method: string, graphql?: IGraphQLInfo): 'high' | 'medium' | 'low' {
    // GraphQL mutation은 높은 우선순위
    if (graphql?.operationType === 'mutation') {
      return 'high';
    }

    // POST, PUT, DELETE는 높은 우선순위
    if (['POST', 'PUT', 'DELETE'].includes(method.toUpperCase())) {
      return 'high';
    }

    // API 엔드포인트인지 확인
    if (url.includes('/api/') || url.includes('/graphql')) {
      return 'medium';
    }

    // 정적 리소스는 낮은 우선순위
    if (/\.(js|css|png|jpg|jpeg|gif|svg|woff|woff2|ttf)(\?|$)/.test(url)) {
      return 'low';
    }

    return 'medium';
  }

  /**
   * Fetch 응답 처리
   */
  private async _handleFetchResponse(response: Response, request: IEnhancedNetworkRequest): Promise<void> {
    try {
      let responseBody: string | undefined;
      let responseBodySize = 0;

      // 응답 바디 처리
      if (this._config.captureResponseBody) {
        const responseClone = response.clone();
        const bodyText = await responseClone.text();
        
        responseBodySize = new TextEncoder().encode(bodyText).length;
        responseBody = bodyText.length > this._config.maxBodySize ? 
          bodyText.substring(0, this._config.maxBodySize) + '...[truncated]' : 
          bodyText;
      }

      // 응답 헤더 추출
      const responseHeaders = this._extractResponseHeaders(response.headers);

      // GraphQL 응답 파싱
      const graphqlResponse = request.graphql ? 
        this._parseGraphQLResponse(responseBody) : undefined;

      const networkResponse: IEnhancedNetworkResponse = {
        id: generateUniqueId(),
        requestId: request.id,
        status: response.status,
        statusText: response.statusText,
        headers: responseHeaders,
        body: responseBody,
        timestamp: Date.now(),
        timing: request.timing,
        size: {
          responseHeaders: this._calculateHeadersSize(responseHeaders),
          responseBody: responseBodySize,
          total: this._calculateHeadersSize(responseHeaders) + responseBodySize,
        },
        graphql: graphqlResponse,
      };

      this._responseHistory.push(networkResponse);

      // 히스토리 크기 제한
      if (this._responseHistory.length > this._config.maxHistorySize) {
        this._responseHistory = this._responseHistory.slice(-this._config.maxHistorySize);
      }

      // 콜백 호출
      if (this._config.onResponse) {
        try {
          this._config.onResponse(networkResponse);
        } catch (error) {
          console.warn('[NetworkInterceptor] Error in onResponse callback:', error);
        }
      }
    } catch (error) {
      console.warn('[NetworkInterceptor] Error handling fetch response:', error);
    }
  }

  /**
   * Fetch 에러 처리
   */
  private async _handleFetchError(error: Error, request: IEnhancedNetworkRequest): Promise<void> {
    const networkResponse: IEnhancedNetworkResponse = {
      id: generateUniqueId(),
      requestId: request.id,
      status: 0,
      statusText: 'Network Error',
      headers: {},
      body: error.message,
      timestamp: Date.now(),
      timing: request.timing,
      size: {
        responseHeaders: 0,
        responseBody: error.message.length,
        total: error.message.length,
      },
    };

    this._responseHistory.push(networkResponse);

    // 에러 콜백 호출
    if (this._config.onError) {
      try {
        this._config.onError(error, request);
      } catch (callbackError) {
        console.warn('[NetworkInterceptor] Error in onError callback:', callbackError);
      }
    }
  }

  /**
   * XHR 응답 처리
   */
  private _handleXHRResponse(xhr: XMLHttpRequest, request: IEnhancedNetworkRequest): void {
    try {
      let responseBody: string | undefined;
      let responseBodySize = 0;

      if (this._config.captureResponseBody) {
        responseBodySize = new TextEncoder().encode(xhr.responseText).length;
        responseBody = xhr.responseText.length > this._config.maxBodySize ? 
          xhr.responseText.substring(0, this._config.maxBodySize) + '...[truncated]' : 
          xhr.responseText;
      }

      // 응답 헤더 추출 (XHR에서는 제한적)
      const responseHeaders: Record<string, string> = {};
      const headerString = xhr.getAllResponseHeaders();
      if (headerString) {
        headerString.split('\r\n').forEach(line => {
          const parts = line.split(': ');
          if (parts.length === 2) {
            responseHeaders[parts[0]] = parts[1];
          }
        });
      }

      // GraphQL 응답 파싱
      const graphqlResponse = request.graphql ? 
        this._parseGraphQLResponse(responseBody) : undefined;

      const networkResponse: IEnhancedNetworkResponse = {
        id: generateUniqueId(),
        requestId: request.id,
        status: xhr.status,
        statusText: xhr.statusText,
        headers: responseHeaders,
        body: responseBody,
        timestamp: Date.now(),
        timing: request.timing,
        size: {
          responseHeaders: this._calculateHeadersSize(responseHeaders),
          responseBody: responseBodySize,
          total: this._calculateHeadersSize(responseHeaders) + responseBodySize,
        },
        graphql: graphqlResponse,
      };

      this._responseHistory.push(networkResponse);

      // 콜백 호출
      if (this._config.onResponse) {
        try {
          this._config.onResponse(networkResponse);
        } catch (error) {
          console.warn('[NetworkInterceptor] Error in onResponse callback:', error);
        }
      }
    } catch (error) {
      console.warn('[NetworkInterceptor] Error handling XHR response:', error);
    }
  }

  /**
   * GraphQL 응답 파싱
   */
  private _parseGraphQLResponse(body?: string): { errors?: unknown[]; data?: unknown; extensions?: Record<string, unknown> } | undefined {
    if (!body) return undefined;

    try {
      const parsed = JSON.parse(body);
      if (parsed.data !== undefined || parsed.errors !== undefined) {
        return {
          errors: parsed.errors,
          data: parsed.data,
          extensions: parsed.extensions,
        };
      }
    } catch (error) {
      // GraphQL 응답이 아님
    }

    return undefined;
  }

  /**
   * 헤더 크기 계산
   */
  private _calculateHeadersSize(headers: Record<string, string>): number {
    return Object.entries(headers).reduce((size, [key, value]) => {
      return size + key.length + value.length + 4; // ": " + "\r\n"
    }, 0);
  }

  /**
   * 요청 헤더 추출
   */
  private _extractHeaders(headers?: HeadersInit): Record<string, string> {
    const result: Record<string, string> = {};

    if (!headers) {
      return result;
    }

    if (headers instanceof Headers) {
      headers.forEach((value, key) => {
        result[key] = value;
      });
    } else if (Array.isArray(headers)) {
      headers.forEach(([key, value]) => {
        result[key] = value;
      });
    } else {
      Object.entries(headers).forEach(([key, value]) => {
        result[key] = value;
      });
    }

    return result;
  }

  /**
   * 응답 헤더 추출
   */
  private _extractResponseHeaders(headers: Headers): Record<string, string> {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  /**
   * 현재 상태 가져오기
   */
  public getState() {
    return {
      isIntercepting: this._isIntercepting,
      requestCount: this._requestHistory.length,
      responseCount: this._responseHistory.length,
      config: { ...this._config },
      metrics: this.getNetworkMetrics(),
    };
  }

  /**
   * 정리
   */
  public destroy(): void {
    this.stopIntercepting();
    this.clearHistory();
  }
}
